/**
 * Shared hex-color parsing for the tree renderers.
 *
 * `hexToRgb` reproduces the historical `PhylocanvasRenderer.hexToRgb` exactly
 * (3-tuple, array passthrough, visible-gray fallback) so phylocanvas.gl keeps
 * its existing colors. `hexToRgba` is the 4-tuple variant the TheiaPhyloViewer
 * SVG library requires, its `normaliseColour` silently renders a CSS/hex string
 * as black, so callers MUST pass `[r,g,b,a]` integer arrays.
 */

const HEX_RE = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

/** Fallback when a color cannot be parsed: a visible mid-gray, never black. */
const FALLBACK_RGB: [number, number, number] = [153, 153, 153];

/**
 * Convert a `#rrggbb`/`rrggbb` string to `[r, g, b]`. A value that is already
 * a numeric array is returned unchanged (phylocanvas.gl passthrough). Invalid
 * input yields a visible gray.
 */
export function hexToRgb(hex: string | number[]): number[] {
  if (Array.isArray(hex)) return hex;

  const result = HEX_RE.exec(hex);
  return result
    ? [parseInt(result[1]!, 16), parseInt(result[2]!, 16), parseInt(result[3]!, 16)]
    : [...FALLBACK_RGB];
}

/**
 * Convert a `#rrggbb`/`rrggbb` string to `[r, g, b, 255]`. Invalid input yields
 * a visible opaque gray. This is the form the TheiaPhyloViewer library needs.
 */
export function hexToRgba(hex: string): [number, number, number, number] {
  const [r, g, b] = hexToRgb(hex) as [number, number, number];
  return [r, g, b, 255];
}
